"use client"

import { motion, useInView } from "framer-motion"
import { useState } from "react"
import Link from "next/link"
import {
  ArrowRight, ClipboardList, Heart, HeartHandshake,
  LayoutDashboard, Truck, Users, Zap, TrendingUp, CheckCircle,
  AlertTriangle, Rocket, Wallet, MessageSquare, Phone, Globe,
  Briefcase, Building2, TreePine, Workflow, ShoppingBag, BookOpen,
} from "lucide-react"
import { useRef } from "react"
import dynamic from "next/dynamic"
import { Suspense } from "react"
import { WhyChooseSection } from "@/components/solutions/why-choose-section"
import { PremiumCard } from "@/components/ui/PremiumCard"
import { Button } from "@/components/ui/Button"
import { ComparisonTable } from "@/components/who-we-serve/ComparisonTable"
import type { WhoWeServeEntry } from "@/data/who-we-serve"
import type { LucideIcon } from "lucide-react"

const ProblemSection = dynamic(() => import("@/components/solutions/problem-section").then(m => ({ default: m.ProblemSection })), { ssr: false })
const FAQSection = dynamic(() => import("@/components/home/FAQSection").then(m => ({ default: m.FAQSection })), { ssr: false })
const CTASection = dynamic(() => import("@/components/home/CTASection").then(m => ({ default: m.CTASection })), { ssr: false })
const ContactCommandCenter = dynamic(() => import("@/components/home/ContactCommandCenter").then(m => ({ default: m.default })), { ssr: false })
const LeadCapturePopup = dynamic(() => import("@/components/forms/LeadCapturePopup").then(m => ({ default: m.default })), { ssr: false })

const ICON_MAP: Record<string, LucideIcon> = {
  Users, Zap, TrendingUp, CheckCircle, Heart,
  ClipboardList, LayoutDashboard, Truck,
  AlertTriangle, Rocket, Wallet, MessageSquare,
  Phone, Globe, Briefcase, Building2, TreePine,
  HeartHandshake, Workflow, ShoppingBag, BookOpen,
}

interface WhoWeServePageContentProps {
  entry: WhoWeServeEntry
}

export function WhoWeServePageContent({ entry }: WhoWeServePageContentProps) {
  const ACCENT = "#2C78B9"

  const [isDemoOpen, setIsDemoOpen] = useState(false)

  const problemForSection = {
    ...entry.problem,
    painPoints: entry.problem.painPoints.map((p) => `${p.title}: ${p.description}`),
  }

  const categoryLabel = entry.hero.badge?.replace("Mx-ERP · ", "") || "your organization"
  const whySubtitle = `Purpose-built for ${categoryLabel} — streamline operations, ensure compliance, and scale with confidence.`

  return (
    <main className="min-h-screen">
      <WhoWeServeHero hero={entry.hero} entry={entry} setIsDemoOpen={setIsDemoOpen} />

      <ProblemSection problem={problemForSection} />

      <section className="relative overflow-hidden bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 md:mb-16 text-center flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EBF5FF] border border-[#D1E9FF] mb-5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2C78B9]" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#2C78B9]">Key Features</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-4 text-balance text-xl sm:text-2xl md:text-4xl font-black tracking-tight text-slate-900 leading-[1.2]"
            >
              Everything You Need <br className="hidden md:block" />
              <span className="text-[#2C78B9]">to Succeed</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {entry.features.map((feature, idx) => {
              const IconComp = feature.icon ? ICON_MAP[feature.icon] : null
              const customIcon = IconComp ? (
                <div className="[&_svg]:h-12 [&_svg]:w-12">
                  <IconComp className="text-[#2C78B9]" strokeWidth={1.5} />
                </div>
              ) : undefined

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.5, ease: "easeOut" }}
                >
                  <PremiumCard
                    customIcon={customIcon}
                    title={feature.title}
                    description={feature.description}
                    tags={feature.tags}
                    meta={feature.meta}
                    stats={feature.stats}
                    color="blue"
                    ctaLabel=""
                  />
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {entry.industries?.length ? (
        <section className="relative overflow-hidden bg-slate-50 py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14 text-center flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EBF5FF] border border-[#D1E9FF] mb-5"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#2C78B9]" />
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#2C78B9]">Industries We Serve</span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="mb-3 text-balance text-xl sm:text-2xl md:text-4xl font-black tracking-tight text-slate-900 leading-[1.2]"
              >
                Built for <span className="text-[#2C78B9]">{categoryLabel}</span> Industry Complexity
              </motion.h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {entry.industries.map((item, idx) => {
                const iconName =
                  item.title.toLowerCase().includes("manufacturing")
                    ? "ClipboardList"
                    : item.title.toLowerCase().includes("construction")
                      ? "Workflow"
                      : item.title.toLowerCase().includes("healthcare")
                        ? "Heart"
                        : item.title.toLowerCase().includes("conglomerate") || item.title.toLowerCase().includes("multi-industry")
                          ? "LayoutDashboard"
                          : item.title.toLowerCase().includes("distribution")
                            ? "Truck"
                            : item.title.toLowerCase().includes("retail")
                              ? "ShoppingBag"
                              : item.title.toLowerCase().includes("professional")
                                ? "Briefcase"
                                : item.title.toLowerCase().includes("foundation") || item.title.toLowerCase().includes("grant")
                                  ? "Building2"
                                  : item.title.toLowerCase().includes("disaster")
                                    ? "AlertTriangle"
                                    : item.title.toLowerCase().includes("environmental")
                                      ? "TreePine"
                                      : item.title.toLowerCase().includes("women") || item.title.toLowerCase().includes("child")
                                        ? "HeartHandshake"
                                        : item.title.toLowerCase().includes("education")
                                          ? "BookOpen"
                                          : "Users"

                const IconComp = ICON_MAP[iconName]

                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08, duration: 0.5, ease: "easeOut" }}
                    className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)] transition-all duration-300"
                  >
                    <div className="mb-4">
                      {IconComp ? (
                        <IconComp className="h-6 w-6 text-[#2C78B9]" />
                      ) : (
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#EBF5FF] text-[10px] font-bold text-[#2C78B9]">
                          {idx + 1}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.slice(0, 6).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      ) : null}

      <ComparisonTable
        title="Why Mx-ERP"
        subtitle="See how Mx-ERP stacks up against generic ERP vendors."
        items={entry.comparison}
        accentColor={ACCENT}
      />

      <WhyChooseSection solution={{ whySubtitle, reasons: entry.reasons } as any} />

      <section className="relative overflow-hidden bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 md:mb-14 text-center flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EBF5FF] border border-[#D1E9FF] mb-5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2C78B9]" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#2C78B9]">
                {entry.slug === "mid-size-business" ? "Growing Businesses Trust Us" : "Enterprise Clients Trust Us"}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-3 text-balance text-xl sm:text-2xl md:text-4xl font-black tracking-tight text-slate-900 leading-[1.2]"
            >
              {entry.trustTitle ?? (entry.slug === "mid-size-business" ? "Built for Growing Teams That Need More Than Basic ERP" : "Proven at Global Enterprise Scale")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="max-w-3xl text-sm md:text-base text-slate-600 leading-relaxed whitespace-pre-line"
            >
              {entry.trustParagraph ?? (entry.slug === "mid-size-business"
                ? "ACGIL has been implementing ERP solutions since 1993 — long before cloud ERP was even a concept. We have worked with mid-size businesses across manufacturing, distribution, retail, construction, and services — helping them transition from spreadsheets and legacy systems to unified, scalable platforms.\n\nOur clients choose us because we understand budget constraints, deliver realistic timelines, configure systems around their workflows, provide training that actually helps teams adopt the system, and stay after go-live — because ERP is a long-term partnership."
                : "ACGIL has implemented Mx-ERP for enterprises across Asia, the Middle East, Africa, and internationally — from multi-location hospital chains to large construction companies managing simultaneous projects across multiple countries. Institutions including AIIMS (Dr. Rajendra Prasad Centre), Honeywell, and international healthcare organisations have trusted us to manage mission-critical operations.\n\nWhen you work with ACGIL, you are not buying a licence. You are gaining a technology partner with the experience to understand your industry, the capability to customise accordingly, and the commitment to stay with you long after go-live.")}
            </motion.p>
          </div>
        </div>
      </section>

      <FAQSection items={entry.faqs} showContact={false} />

      <ContactCommandCenter id="contact-form" />

      <LeadCapturePopup open={isDemoOpen} onOpenChange={setIsDemoOpen} />

      <CTASection
        title={entry.cta.title}
        description={entry.cta.description || ""}
        primaryLabel={entry.cta.primaryButton}
        secondaryLabel={entry.cta.secondaryButton}
        primaryHref="/contact"
        secondaryHref="#contact-form"
        onSecondaryClick={() => {
          const el = document.getElementById("contact-form");
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
    </main>
  )
}

function WhoWeServeHeroInner({ hero, entry, setIsDemoOpen }: { hero: WhoWeServeEntry["hero"]; entry: WhoWeServeEntry; setIsDemoOpen: (v: boolean) => void }) {
  const containerRef = useRef(null)
  const isInView = useInView(containerRef, { once: true })
  const [expanded, setExpanded] = useState(false)

  const firstParagraph = hero.description.split("\n\n")[0]
  const restParagraphs = hero.description.split("\n\n").slice(1)

  const titleParts = hero.title.split("—")

  return (
    <section
      ref={containerRef}
      className="relative flex items-center overflow-hidden pt-16 pb-14 sm:pt-20 sm:pb-16 lg:pt-24 lg:pb-20 bg-[linear-gradient(135deg,#0f3f9c_0%,#1065b0_48%,#12a3bd_100%)]"
    >
      <div className="relative z-10 w-full mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/10 text-white mb-6 sm:mb-8"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
            <span className="text-xs font-bold uppercase tracking-[0.08em]">
              {hero.badge ?? "Mx-ERP"}
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-[1.6rem] sm:text-4xl lg:text-[2.6rem] font-black text-white mb-5 sm:mb-6 leading-[1.15] tracking-tight"
          >
            {titleParts.length > 1 ? (
              <>
                {titleParts.slice(0, -1).join("—")}
                <br className="hidden sm:block" />
                {titleParts.at(-1)}
              </>
            ) : (
              hero.title
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="text-[13px] sm:text-[14px] md:text-[15px] text-white/90 mb-4 leading-[1.8] max-w-[640px] mx-auto"
          >
            {firstParagraph}
          </motion.p>

          {expanded && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-[640px] mx-auto space-y-4"
            >
              {restParagraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-[13px] sm:text-[14px] md:text-[15px] text-white/90 leading-[1.8]"
                >
                  {p}
                </p>
              ))}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="mt-6 flex items-center justify-center gap-4"
          >
            <Link
              href="#contact-form"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("contact-form");
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="group inline-flex items-center justify-center gap-2 rounded-full px-6 h-10 bg-white text-[#2C78B9] border-none transition-all duration-200 hover:scale-[1.02] hover:bg-white/90 hover:shadow-lg font-semibold text-sm whitespace-nowrap"
            >
              <span className="transition-transform duration-200 group-hover:-translate-x-0.5">
                Talk to an Expert
              </span>
              <span className="hidden sm:inline-flex items-center transition-transform duration-200 sm:w-0 sm:opacity-0 sm:-translate-x-2 sm:group-hover:w-4 sm:group-hover:opacity-100 sm:group-hover:translate-x-1">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Button
              size="lg"
              type="button"
              onClick={() => setIsDemoOpen(true)}
              className="group flex inline-flex items-center justify-center rounded-full px-6 h-10 border border-white/60 text-white hover:bg-white/10 transition-all duration-200 hover:scale-[1.02] font-semibold text-sm overflow-hidden"
            >
              <span className="transition-transform duration-200 group-hover:-translate-x-0.5">
                Request Free Demo
              </span>
              <span className="hidden sm:inline-flex items-center transition-transform duration-200 sm:w-0 sm:opacity-0 sm:-translate-x-2 sm:group-hover:w-4 sm:group-hover:opacity-100 sm:group-hover:translate-x-1">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export function WhoWeServeHero({ hero, entry, setIsDemoOpen }: { hero: WhoWeServeEntry["hero"]; entry: WhoWeServeEntry; setIsDemoOpen: (v: boolean) => void }) {
  return <WhoWeServeHeroInner hero={hero} entry={entry} setIsDemoOpen={setIsDemoOpen} />
}
